import { createApp, nextTick } from 'vue'
import { createPinia } from 'pinia'
import { createRouter, createWebHistory } from 'vue-router'
import App from './App.vue'
import { i18n } from './i18n'
import './style.css'
// CSS base de v-calendar queda global para preservar el orden de cascada
// con los overrides .vc-* de style.css; el JS del plugin carga lazy
import 'v-calendar/style.css'

// Vistas eager: las públicas críticas y la página de error
// (Error debe estar en el bundle inicial para renderizar aunque falle la carga de un chunk)
import Home from './views/Home.vue'
import Login from './views/Login.vue'
import ErrorPage from './views/Error.vue'

// El resto se carga lazy: cada view genera su propio chunk
const Dashboard = () => import('./views/Dashboard.vue')
const CalendarView = () => import('./views/CalendarView.vue')
const CitasView = () => import('./views/CitasView.vue')
const AppointmentsAuditView = () => import('./views/AppointmentsAuditView.vue')
const Register = () => import('./views/Register.vue')
const AuthCallback = () => import('./views/AuthCallback.vue')
const ImpersonationEntry = () => import('./views/ImpersonationEntry.vue')
const GoogleCalendarCallback = () => import('./views/GoogleCalendarCallback.vue')
const ProfileSettings = () => import('./views/ProfileSettings.vue')
const UpdatesView = () => import('./views/UpdatesView.vue')
const NotificationsView = () => import('./views/NotificationsView.vue')
const CompaniesSettings = () => import('./views/CompaniesSettings.vue')
const CompanyInformationView = () => import('./views/CompanyInformationView.vue')
const Subscription = () => import('./views/Subscription.vue')
const Clients = () => import('./views/Clients.vue')
const ClientDetailView = () => import('./views/ClientDetailView.vue')
const Services = () => import('./views/Services.vue')
const PromotionsView = () => import('./views/PromotionsView.vue')
const PromotionFormView = () => import('./views/PromotionFormView.vue')
const Support = () => import('./views/Support.vue')
const SupportTicketDetail = () => import('./views/SupportTicketDetail.vue')
const ServiceOrders = () => import('./views/ServiceOrders.vue')
const CobrosView = () => import('./views/CobrosView.vue')
const ReportsPage = () => import('./views/ReportsPage.vue')
const ServiceOrderTracking = () => import('./views/public/ServiceOrderTracking.vue')
const VerifyEmail = () => import('./views/VerifyEmail.vue')
const TermsAndConditions = () => import('./views/TermsAndConditions.vue')
const PrivacyPolicy = () => import('./views/PrivacyPolicy.vue')
const OnboardingView = () => import('./views/OnboardingView.vue')

// Importar guards de autenticación
import { requireAuth, requireGuest, requireOnboarding, initAuth } from './router/guards.js'
import { useRouteLoading } from './composables/useRouteLoading.js'

const { startRouteLoading, stopRouteLoading } = useRouteLoading()
// Captura del prompt de instalación PWA (beforeinstallprompt se dispara temprano)
import { initPwaInstall } from './composables/usePwaInstall'
import { initNativeDeepLinks } from './utils/nativeDeepLinks.js'

initPwaInstall()

// Definir rutas
const routes = [
  { 
    path: '/', 
    name: 'Home', 
    component: Home 
  },
  { 
    path: '/login', 
    name: 'Login', 
    component: Login,
    meta: { guest: true },
    beforeEnter: requireGuest
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
    meta: { guest: true },
    beforeEnter: requireGuest
  },
  {
    path: '/forgot-password',
    component: () => import('./views/ForgotPassword.vue'),
    meta: { guest: true },
    beforeEnter: requireGuest
  },
  {
    path: '/reset-password/:token?',
    component: () => import('./views/ResetPassword.vue'),
    meta: { guest: true },
    beforeEnter: requireGuest
  },
  {
    path: '/accept-invitation',
    component: () => import('./views/AcceptInvitation.vue'),
    meta: { guest: true },
    beforeEnter: requireGuest
  },
  {
    path: '/welcome',
    component: () => import('./views/WorkerWelcome.vue')
  },
  {
    path: '/onboarding',
    name: 'Onboarding',
    component: OnboardingView,
    beforeEnter: requireAuth
  },
  {
    path: '/verify-email',
    component: VerifyEmail
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: AuthCallback
  },
  {
    // Handoff de suplantación desde el panel admin. `public: true` para saltar
    // initAuth global — esta vista establece su propia sesión de solo lectura.
    path: '/impersonar',
    name: 'ImpersonationEntry',
    component: ImpersonationEntry,
    meta: { public: true, requiresAuth: false }
  },
  {
    path: '/google-calendar/callback',
    name: 'GoogleCalendarCallback',
    component: GoogleCalendarCallback
  },
  { 
    path: '/dashboard', 
    name: 'Dashboard', 
    component: Dashboard,
    meta: { permission: 'dashboard-view' },
    beforeEnter: requireAuth
  },
  { 
    path: '/dashboard/calendar', 
    name: 'DashboardCalendar', 
    component: CalendarView,
    meta: { permission: 'appointments-view' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/citas',
    name: 'Citas',
    component: CitasView,
    meta: { permission: 'appointments-view' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/citas/bitacora',
    name: 'CitasBitacora',
    component: AppointmentsAuditView,
    meta: { permission: 'appointments-view' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/updates',
    name: 'Updates',
    component: UpdatesView,
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/notifications',
    name: 'Notifications',
    component: NotificationsView,
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/clients',
    name: 'DashboardClients', 
    component: Clients,
    meta: { permission: 'clients-view' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/clients/:id',
    name: 'ClientDetail',
    component: ClientDetailView,
    meta: { permission: 'client-record-view' },
    beforeEnter: requireAuth
  },
  { 
    path: '/dashboard/services', 
    name: 'DashboardServices', 
    component: Services,
    meta: { permission: 'services-view' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/services/promotions',
    name: 'Promotions',
    component: PromotionsView,
    meta: { permission: 'promotions-view' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/services/promotions/new',
    name: 'PromotionNew',
    component: PromotionFormView,
    meta: { permission: 'promotions-manage' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/services/promotions/:id(\\d+)/edit',
    name: 'PromotionEdit',
    component: PromotionFormView,
    meta: { permission: 'promotions-manage' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/service-orders',
    name: 'ServiceOrders',
    component: ServiceOrders,
    meta: { permission: 'pipeline-view' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/cobros',
    name: 'Cobros',
    component: CobrosView,
    meta: { permission: 'payments-view' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/support',
    name: 'Support',
    component: Support,
    meta: { permission: 'support-tickets-view' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/support/:id',
    name: 'SupportTicketDetail',
    component: SupportTicketDetail,
    meta: { permission: 'support-tickets-view' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/reports',
    name: 'DashboardReports',
    component: ReportsPage,
    meta: { permission: 'reports-basic' },
    beforeEnter: requireAuth
  },
  {
    // Bandeja de conversaciones de WhatsApp (handoff a humano)
    path: '/dashboard/whatsapp',
    name: 'WhatsAppInbox',
    component: () => import('./views/WhatsAppInboxView.vue'),
    beforeEnter: requireAuth
  },
  {
    // Conversación individual (desde la bandeja o desde el enlace del correo de handoff)
    path: '/dashboard/whatsapp/:id',
    name: 'WhatsAppConversation',
    component: () => import('./views/WhatsAppConversationView.vue'),
    beforeEnter: requireAuth
  },
  {
    // El backend manda ${FRONTEND_URL}/whatsapp/conversations/:id en el correo de handoff.
    // Sin sesión, requireAuth lleva a /login?redirect=… y tras el login vuelve aquí.
    path: '/whatsapp/conversations/:id',
    redirect: (to) => ({ path: `/dashboard/whatsapp/${to.params.id}` })
  },
  {
    // Importaciones ahora vive como pestaña de Configuración de empresa; la gate de admin
    // (antes meta.permission) la aplica esa pestaña (ver useCompanyInformation.js).
    path: '/dashboard/imports',
    redirect: '/dashboard/settings/companies?tab=imports'
  },
  { 
    path: '/dashboard/settings', 
    name: 'DashboardSettings', 
    component: Dashboard,
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/settings/profile',
    name: 'ProfileSettings',
    component: ProfileSettings,
    meta: { permission: 'profile-edit' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/settings/business',
    redirect: '/dashboard/settings/companies'
  },
  {
    path: '/dashboard/settings/business/users',
    redirect: '/dashboard/settings/companies?tab=team'
  },
  {
    path: '/dashboard/settings/business/roles',
    redirect: '/dashboard/settings/companies?tab=roles'
  },
  {
    path: '/dashboard/settings/companies',
    name: 'CompaniesSettings',
    component: CompanyInformationView,
    meta: { permission: 'company-view' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/settings/companies/list',
    name: 'CompaniesList',
    component: CompaniesSettings,
    meta: { permission: 'company-switch' },
    beforeEnter: requireAuth
  },
  {
    path: '/dashboard/settings/subscription',
    name: 'Subscription',
    component: Subscription,
    meta: { permission: 'subscription-view' },
    beforeEnter: requireAuth
  },
  {
    path: '/subscription/success',
    redirect: (to) => ({
      path: '/dashboard/settings/subscription',
      query: { ...to.query, tab: 'actual', checkout: 'success' }
    })
  },
  {
    path: '/subscription/cancel',
    redirect: (to) => ({
      path: '/dashboard/settings/subscription',
      query: { ...to.query, tab: 'planes', checkout: 'cancel' }
    })
  },
  {
    // Resumen público de una cita (enlace del recordatorio de WhatsApp).
    // Más específica que /booking/:bookingToken: va antes por claridad.
    path: '/booking/appointment/:appointmentToken',
    name: 'PublicAppointmentSummary',
    component: () => import('./views/public/AppointmentSummaryPage.vue'),
    meta: {
      public: true,
      requiresAuth: false
    }
  },
  {
    path: '/booking/:bookingToken',
    name: 'PublicBooking',
    component: () => import('./views/public/PublicBookingPage.vue'),
    meta: {
      public: true,
      requiresAuth: false
    }
  },
  {
    path: '/service-order/track/:token',
    name: 'ServiceOrderTracking',
    component: ServiceOrderTracking,
    meta: { public: true, requiresAuth: false }
  },
  {
    path: '/terminos-y-condiciones',
    name: 'TermsAndConditions',
    component: TermsAndConditions
  },
  {
    path: '/privacidad',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy
  },
  // Página de error
  {
    path: '/error',
    name: 'Error',
    component: ErrorPage,
    props: route => ({
      type: route.query.type || '404',
      message: route.query.message || '',
      details: route.query.details || '',
      source: route.query.source || ''
    })
  },
  // Rutas no encontradas -> página de error 404: la ruta pedida (no "/error") delata el origen.
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: ErrorPage,
    props: route => ({ type: '404', source: route.path.startsWith('/dashboard') ? 'app' : 'site' })
  }
]

function scrollAppToTop() {
  document.querySelector('.dashboard-main')?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
}

// Crear router
const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return nextTick().then(() => ({ el: to.hash, behavior: 'smooth', top: 72 }))
    }
    return nextTick().then(() => {
      scrollAppToTop()
      return { top: 0, left: 0, behavior: 'auto' }
    })
  }
})

// Global navigation guard para inicializar autenticación
router.beforeEach(async (to, from, next) => {
  if (to.meta.public) {
    next()
    return
  }

  if (!router.authInitialized) {
    await initAuth()
    router.authInitialized = true
  }

  next()
})

// Onboarding sin completar → redirige a /onboarding solo dentro del área protegida (dashboard/subscription)
router.beforeEach(requireOnboarding)

// Crear aplicación
const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)
app.use(i18n)

initNativeDeepLinks(router)

// ─── Auto-recuperación tras un deploy nuevo ──────────────────────────────────
// Cuando se publica una versión nueva, los chunks lazy cambian de hash. Una
// pestaña abierta (o el Service Worker en autoUpdate) puede tener el index.html
// viejo y al navegar intenta importar un chunk que ya no existe: el server
// devuelve index.html (text/html) y el import() falla. Recargamos una vez para
// bajar el index.html y los chunks frescos. Guard anti-loop por si el fallo
// persiste (server caido, etc.).
function isChunkLoadError(reason) {
  const msg = String(reason?.message || reason || '')
  return /dynamically imported module|Importing a module script failed|Failed to fetch dynamically|ChunkLoadError|Loading chunk .* failed/i.test(msg)
}

function reloadOnceForStaleChunks() {
  const KEY = 'app:lastChunkReload'
  const last = Number(sessionStorage.getItem(KEY) || 0)
  if (Date.now() - last < 15000) return // ya recargamos hace poco: evitar loop
  sessionStorage.setItem(KEY, String(Date.now()))
  window.location.reload()
}

// Hook nativo de Vite cuando falla la precarga de un módulo dinámico.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  reloadOnceForStaleChunks()
})

// Fallo al cargar el chunk de una ruta lazy (navegación de Vue Router).
router.onError((error) => {
  stopRouteLoading()
  if (isChunkLoadError(error)) reloadOnceForStaleChunks()
})

// Empresa activa vive bajo /dashboard: un crash ahí es la app, no el sitio público.
function errorSourceFromCurrentRoute() {
  return router.currentRoute.value.path.startsWith('/dashboard') ? 'app' : 'site'
}

// Error handler global para errores de Vue
app.config.errorHandler = (err, instance, info) => {
  console.error('Error global de Vue:', err)
  console.error('Componente:', instance)
  console.error('Info:', info)

  const source = errorSourceFromCurrentRoute()
  // Solo redirigir a la página de error en producción
  // En desarrollo, mostrar el error en consola para debugging
  if (import.meta.env.PROD) {
    router.push({
      path: '/error',
      query: {
        type: 'error',
        message: 'Ha ocurrido un error inesperado',
        source
      }
    })
  } else {
    // En desarrollo, también navegar pero con detalles
    router.push({
      path: '/error',
      query: {
        type: 'error',
        message: err.message || 'Error desconocido',
        details: err.stack || '',
        source
      }
    })
  }
}

// Handler para errores no capturados de promesas
window.addEventListener('unhandledrejection', (event) => {
  console.error('Promise rechazada no manejada:', event.reason)

  // Chunk lazy obsoleto tras un deploy: recargar para tomar la versión nueva.
  if (isChunkLoadError(event.reason)) {
    event.preventDefault()
    reloadOnceForStaleChunks()
    return
  }

  // Verificar si es un error de timeout o de red
  if (event.reason?.name === 'TimeoutError' || event.reason?.message?.includes('timeout')) {
    router.push({
      path: '/error',
      query: { type: 'timeout', source: errorSourceFromCurrentRoute() }
    })
  }
})

// Timeout para navegación 
let navigationTimeout = null
const NAVIGATION_TIMEOUT = 15000 // 15 segundos

router.beforeEach((to, from, next) => {
  // Indicador de carga de navegación (chunk lazy de la vista destino)
  startRouteLoading()

  // Limpiar timeout anterior
  if (navigationTimeout) {
    clearTimeout(navigationTimeout)
  }

  // Configurar timeout para la navegación (excepto para la página de error)
  if (to.name !== 'Error' && to.name !== 'NotFound') {
    navigationTimeout = setTimeout(() => {
      console.warn('Navegación timeout: la página tardó demasiado en cargar')
      router.push({
        path: '/error',
        query: { type: 'timeout', source: to.path.startsWith('/dashboard') ? 'app' : 'site' }
      })
    }, NAVIGATION_TIMEOUT)
  }
  
  next()
})

router.afterEach(() => {
  // Ocultar indicador de carga al terminar la navegación
  stopRouteLoading()

  // Limpiar timeout después de navegación exitosa
  if (navigationTimeout) {
    clearTimeout(navigationTimeout)
    navigationTimeout = null
  }
})

app.mount('#app')

const removeAppLoader = () => {
  const loader = document.getElementById('app-loading')
  if (!loader) return
  loader.style.opacity = '0'
  setTimeout(() => loader.remove(), 300)
}
router.isReady().then(removeAppLoader).catch(removeAppLoader)
setTimeout(removeAppLoader, 5000)

